import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Button, ButtonGroup, Icon, IconButton, Menu, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconMoreFilled } from '@zillow/constellation-icons';
import { LeaseStatus, useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { useIsFetching, useIsMutating } from '@zg-rentals/react-query5';

import type LeaseAction from '../../models/LeaseAction';
import type { Lease, Property } from '../../models';
import { useWebAnalyticsForListingQuery } from '../../utils/queries';
import * as ActionComponents from './Actions/components';
import {
  isInMobileFromApp,
  onDownloadSignedPdfHandoff,
  onPrepareForLeaseSigningMobileHandoff,
} from '../../utils/mobileHandoffFunctions';
import { MOBILE_HANDOFF_TYPES } from '../../constants/stringConstants';
import { LeaseTypes } from '../../models/Types';
import { LEASE_ACTION_TYPES } from './Actions';
import {
  emitButtonForOverflowActiveEvent,
  emitButtonForOverflowCompleteDraftEvent,
  emitButtonForOverflowIncompleteDraftEvent,
} from '../../../analytics/events';
import { useLeasingUrlParams } from '../../hooks';

type Props = {
  className?: string;
  leaseActions: Array<LeaseAction>;
  lease?: Lease;
  property?: Property;
  initialActiveAction?: LeaseAction;
  fluid?: boolean;
  disabled?: boolean;
  type?: string;
  onPrimaryActionClick?(): void;
  additionalButtons?: React.ReactNode;
  isTop?: boolean;
  inPreview?: boolean;
  onSaveClick?: () => void;
  saveInProgress?: boolean;
};

export function LeaseActionButtonGroup({
  initialActiveAction,
  lease,
  leaseActions,
  fluid,
  disabled,
  type,
  property,
  onPrimaryActionClick,
  additionalButtons,
  isTop,
  saveInProgress,
  onSaveClick,
  ...rest
}: Props): JSX.Element {
  const history = useHistory();
  const isFetching = useIsFetching() > 0;
  const isMutating = useIsMutating() > 0;
  const isInMobileFromAppValue = isInMobileFromApp();
  const { user } = useRentalManagerUser();
  const [activeAction, setActiveAction] = useState(initialActiveAction);
  const { enqueueToast } = useToast();
  const { propertyIdOrUndefined: propertyId } = useLeasingUrlParams();
  const webAnalyticsQuery = useWebAnalyticsForListingQuery(propertyId, { enabled: !!propertyId });
  const zrm_listing_info = webAnalyticsQuery.data;

  const onAtLeastOneSignatureToast = useCallback(() => {
    enqueueToast(
      <Toast tone="warning">Place at least 1 signature placeholder for each person to request signatures</Toast>,
    );
  }, [enqueueToast]);

  function terminateActiveAction(): void {
    setActiveAction(null);
  }

  useEffect(() => {
    const primaryAction = leaseActions?.[0];
    // Specifically to show request signature modal for self uploaded leases after making sure save is finished.
    if (
      saveInProgress === false &&
      !primaryAction?.isDisabled &&
      primaryAction?.name === LEASE_ACTION_TYPES.REQUEST_SIGNATURES &&
      lease?.leaseType === LeaseTypes.SELF_UPLOAD
    ) {
      onPrimaryActionClick?.();
      getLeaseActionHandler(primaryAction)();
    } else if (
      saveInProgress === false &&
      primaryAction?.isDisabled &&
      primaryAction?.name === LEASE_ACTION_TYPES.REQUEST_SIGNATURES &&
      lease?.leaseType === LeaseTypes.SELF_UPLOAD
    ) {
      onAtLeastOneSignatureToast();
    }
  }, [saveInProgress, leaseActions?.[0]?.isDisabled]);

  function onOverflowButtonClick(): void {
    if (lease?.status === LeaseStatus.ACTIVE) {
      emitButtonForOverflowActiveEvent({ user, listing: zrm_listing_info });
    } else if (lease?.status === LeaseStatus.INCOMPLETE_DRAFT) {
      emitButtonForOverflowIncompleteDraftEvent({ user, listing: zrm_listing_info });
    } else if (lease?.status === LeaseStatus.COMPLETE_DRAFT) {
      emitButtonForOverflowCompleteDraftEvent({ user, listing: zrm_listing_info });
    }
  }

  function getPrimaryActionHandler(clickedOnAction: LeaseAction): () => void {
    // ZRM app and Rentals app use different ways to download PDF, do you not use isInMobileWebView for below condition.
    if (isInMobileFromAppValue && clickedOnAction.getMobileHandoffValue() === MOBILE_HANDOFF_TYPES.DOWNLOAD_PDF) {
      return () => {
        onDownloadSignedPdfHandoff(lease?.getPDFLink(), enqueueToast);
      };
    }
    return () => {
      if (
        clickedOnAction.name === LEASE_ACTION_TYPES.REQUEST_SIGNATURES &&
        lease?.leaseType === LeaseTypes.SELF_UPLOAD
      ) {
        onSaveClick();
      } else {
        onPrimaryActionClick?.();
        getLeaseActionHandler(clickedOnAction)();
      }
    };
  }

  function getLeaseActionHandler(clickedOnAction: LeaseAction, isFromOverflow = false): () => void {
    if (
      isFromOverflow &&
      isInMobileFromAppValue &&
      clickedOnAction.getMobileHandoffValue() === MOBILE_HANDOFF_TYPES.DOWNLOAD_PDF
    ) {
      return () => {
        onDownloadSignedPdfHandoff(lease?.getPDFLink(), enqueueToast);
      };
    }

    return () => {
      if (isFromOverflow) {
        onPrimaryActionClick?.();
      }

      setActiveAction(clickedOnAction);
      const emitAnalyticsEvent = clickedOnAction.getEmitAnalyticsEvent();
      if (emitAnalyticsEvent) {
        const leaseWithNumericId = { ...lease, id: Number(lease.id) };
        if (typeof emitAnalyticsEvent === 'function') {
          emitAnalyticsEvent({ user, listing: zrm_listing_info, lease: leaseWithNumericId });
        } else if (isTop) {
          emitAnalyticsEvent.top?.({ user, listing: zrm_listing_info, lease: leaseWithNumericId });
        } else if (isTop === false) {
          emitAnalyticsEvent.bottom?.({ user, listing: zrm_listing_info, lease: leaseWithNumericId });
        }
      }

      if (clickedOnAction.getMobileHandoffValue()) {
        onPrepareForLeaseSigningMobileHandoff(clickedOnAction.getMobileHandoffValue());
      }
    };
  }

  function renderPrimaryLeaseAction(): React.ReactNode {
    const primaryLeaseAction = leaseActions[0];
    const isRequestSignatureButtonInSelfUpload =
      lease?.leaseType === LeaseTypes.SELF_UPLOAD && primaryLeaseAction.name === LEASE_ACTION_TYPES.REQUEST_SIGNATURES;

    if (leaseActions.length === 1 && type === 'link') {
      const icon = primaryLeaseAction.getIcon();
      const iconPos = primaryLeaseAction.getIconPosition();
      return (
        <TextButton
          disabled={isFetching || primaryLeaseAction.isDisabled || activeAction === primaryLeaseAction || disabled}
          onClick={getPrimaryActionHandler(primaryLeaseAction)}
          data-test="primary-cta"
          tone="brand"
          {...(icon && {
            icon,
            iconPosition: iconPos === 'right' ? 'end' : 'start',
          })}
        >
          {primaryLeaseAction.getLabel()}
        </TextButton>
      );
    } else if (isRequestSignatureButtonInSelfUpload) {
      const icon = primaryLeaseAction.getIcon();
      const iconPos = primaryLeaseAction.getIconPosition();
      const showIcon = icon && iconPos === 'right';

      return (
        <Button
          loading={isFetching || activeAction === primaryLeaseAction || !!saveInProgress}
          // Buttons in Constellation 10 do not like null passed into loading.
          disabled={disabled || isMutating}
          onClick={getPrimaryActionHandler(primaryLeaseAction)}
          tone="brand"
          emphasis="filled"
          data-test="primary-cta"
          {...(showIcon && {
            icon,
            iconPosition: 'end',
          })}
        >
          {primaryLeaseAction.getLabel()}
        </Button>
      );
    } else {
      const isPrimary = !type || type === 'primary';
      const icon = primaryLeaseAction.getIcon();
      const iconPos = primaryLeaseAction.getIconPosition();
      // Only show icons on primary buttons when positioned on the right (end)
      const showIcon = icon && iconPos === 'right';
      return (
        <Button
          disabled={
            isFetching || primaryLeaseAction.isDisabled || activeAction === primaryLeaseAction || disabled || isMutating
          }
          onClick={getPrimaryActionHandler(primaryLeaseAction)}
          tone="brand"
          emphasis={isPrimary ? 'filled' : 'outlined'}
          data-test="primary-cta"
          {...(showIcon && {
            icon,
            iconPosition: 'end',
          })}
        >
          {primaryLeaseAction.getLabel()}
        </Button>
      );
    }
  }

  function renderSecondaryLeaseActions(): React.ReactNode {
    return (
      <Menu
        trigger={
          <IconButton
            tone="brand"
            emphasis="outlined"
            title="secondary-actions"
            data-test="secondary-actions"
            disabled={isFetching || disabled || isMutating}
            onClick={onOverflowButtonClick}
          >
            <Icon>
              <IconMoreFilled />
            </Icon>
          </IconButton>
        }
      >
        {leaseActions.slice(1).map((callToAction): React.ReactNode => {
          return callToAction.isDisabled || activeAction === callToAction ? null : (
            <Menu.Item
              key={callToAction.getLabel()}
              disabled={callToAction.isDisabled || activeAction === callToAction}
              onClick={getLeaseActionHandler(callToAction, true)}
            >
              {callToAction.getIcon() && <Menu.ItemIcon render={callToAction.getIcon()} />}
              <Menu.ItemLabel>{callToAction.getLabel()}</Menu.ItemLabel>
            </Menu.Item>
          );
        })}
      </Menu>
    );
  }

  function renderActionComponent(): React.ReactNode {
    if (activeAction == null) {
      return null;
    }

    const ActionComponent = ActionComponents[activeAction.getComponent()];

    return (
      ActionComponent && (
        <ActionComponent
          history={history}
          terminateActiveAction={terminateActiveAction}
          lease={lease}
          property={property}
        />
      )
    );
  }

  if (leaseActions == null || leaseActions.length === 0) {
    return null;
  }

  return (
    <>
      <ButtonGroup {...rest} aria-label="lease actions">
        {additionalButtons}
        {renderPrimaryLeaseAction()}
        {leaseActions.length > 1 && renderSecondaryLeaseActions()}
      </ButtonGroup>

      {/*
          The assumption with renderActionComponent is that it will either return null or it will
          return a modal. While it might be odd to put the modal here, modals don't care where they're placed
          and will expand to fill the entire screen.
        */}
      {renderActionComponent()}
    </>
  );
}

export default LeaseActionButtonGroup;
